<script setup lang="ts">
import { NodeViewContent, nodeViewProps, NodeViewWrapper } from "@tiptap/vue-3";
import { createFormBlock } from "~/stores/mail-block-editor/defaults";
import {
    MailMjmlColumn,
    MailMjmlSection,
} from "#components";

const store = useMailStore();
const fontStore = useFontStore();
const props = defineProps(nodeViewProps);
const theme = computed(() => {
    const theme = store.fieldCollection.replaceContent(
        createFormBlock({
            ...props.node.attrs,
            width: props.node.attrs?.width ?? theme?.width,
            ratingLabelMin: store.formField?.metadata?.min_label,
            ratingLabelMid: store.formField?.metadata?.mid_label,
            ratingLabelMax: store.formField?.metadata?.max_label,
        }),
    );

    const spacingBefore = store.getSpacingBeforeId(props.node.attrs.id);

    const topLeft =
        props.node.attrs.borderTopLeftRadius ?? theme?.borderTopLeftRadius;
    const bottomLeft =
        props.node.attrs.borderBottomLeftRadius ??
        theme?.borderBottomLeftRadius;
    const topRight =
        props.node.attrs.borderTopRightRadius ?? theme?.borderTopRightRadius;
    const bottomRight =
        props.node.attrs.borderBottomRightRadius ??
        theme?.borderBottomRightRadius;

    let numbers;

    if (store.formField?.type === "rating") {
        if (store.formField.sub_type === "nps") {
            numbers = useRange(0, 11);
        } else {
            numbers = useRange(
                store.formField?.metadata?.min_value ?? 1,
                (store.formField?.metadata?.max_value ?? 1) + 1,
            );
        }
    } else {
        numbers = useRange(theme.ratingStart ?? 1, (theme.ratingEnd ?? 10) + 1);
    }

    let right = parseInt(theme.paddingRight);
    let left = parseInt(theme.paddingLeft);

    if (
        (theme.preview === "rating" || store.formField?.type === "rating") &&
        numbers.length > 8
    ) {
        if (right > 10) right = 10;
        if (left > 10) left = 10;
    }

    if (
        theme.ratingInverted ||
        (store.formField?.type === "rating" &&
            store.formField?.metadata?.reverse)
    ) {
        numbers = numbers.reverse();
    }

    const space = 600 - spacingBefore.paddingLeft - spacingBefore.paddingRight;
    const ratingGap = 10;
    const ratingWidth =
        (space - (numbers.length - 1) * ratingGap) / numbers.length;

    return {
        ...theme,
        fontFamily: fontStore.findFontFamily(
            props.node.attrs?.fontFamily ?? theme?.fontFamily,
        )?.class,
        borderRadius: `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`,
        numbers,
        ratingGap: `${ratingGap}px`,
        ratingWidth: `${ratingWidth}px`,
        innerPadding: `${theme.paddingTop} ${right}px ${theme.paddingBottom} ${left}px`,
    };
});
const { t } = useI18n();
const labels = computed(() => {
    let min,
        max,
        mid = null;

    if (store.formField?.sub_type === "nps") {
        min = t("form.labels.nps.min_label");
        max = t("form.labels.nps.max_label");
    } else {
        min = theme.value.ratingLabelMin;
        mid = theme.value.ratingLabelMid;
        max = theme.value.ratingLabelMax;
    }

    return [
        ["left", min?.toUpperCase()],
        ["center", mid?.toUpperCase()],
        ["right", max?.toUpperCase()],
    ];
});
</script>

<template>
    <NodeViewWrapper class="form" @click.stop="store.focusedJsonId = node.attrs.id">
        <MailMjmlSection :style="{
            display: theme.showQuestion && store.formField?.label ? undefined : 'none',
        }" :css-class="node.attrs.identifier" :padding-top="theme.marginTop as string" :contenteditable="false">
            <MailMjmlText :align="theme.alignment" :font-size="theme.fontSize" :font-weight="600"
                :line-height="theme.lineHeight?.toString()" padding-bottom="10px"
                :letter-spacing="theme.letterSpacing?.toString()">
                <HdInput :key="`${store.formField?.label}-${store.recall.fields.length}`" disabled
                    :model-value="store.formField?.label" :fields="store.recall.fields"
                    :extensions="[store.recall.mention]" :mention-match="[store.recall.match]" variant="none"
                    mode="text" :padded="false" unstyled />
            </MailMjmlText>
        </MailMjmlSection>
        <MailMjmlSection :css-class="node.attrs.identifier" :padding-top="theme.showQuestion && store.formField?.label
            ? undefined
            : (theme.marginTop as string)
            " :container-class="store.formField?.type === 'rating' ? 'flex' : undefined">
            <!-- Choice Options -->
            <MailMjmlColumn v-for="option in store.formField?.options" :key="option.id" :style="{
                display: store.formField?.type !== 'choice' || !store.formField?.options?.length ? 'none' : undefined,
            }" width="100%" :contenteditable="false" class="relative">
                <MailMjmlButton class="[&_p]:!m-0" :background-color="theme.backgroundColor" :color="theme.textColor"
                    :inner-padding="theme.innerPadding" :width="theme.width" :align="theme.alignment"
                    :padding-bottom="theme.marginBottom as string" :class="theme.fontFamily"
                    :border-radius="theme.borderRadius as string" :font-size="theme.fontSize"
                    :font-weight="theme.fontWeight?.toString()" :line-height="theme.lineHeight?.toString()"
                    :letter-spacing="theme.letterSpacing?.toString()">
                    <HdInput :key="`${option.label}-${store.recall.fields.length}`" disabled :model-value="option.label"
                        :fields="store.recall.fields" :extensions="[store.recall.mention]"
                        :mention-match="[store.recall.match]" variant="none" mode="text" :padded="false" unstyled />
                </MailMjmlButton>
            </MailMjmlColumn>
            <!-- Rating Numbers -->
            <template v-for="(index, idx) in theme.numbers" :key="index">
                <MailMjmlColumn :style="{ display: idx > 0 && store.formField?.type === 'rating' ? undefined : 'none' }"
                    :contenteditable="false" :width="theme.ratingGap" />
                <MailMjmlColumn :style="{ display: store.formField?.type === 'rating' ? undefined : 'none' }"
                    :contenteditable="false" :width="theme.ratingWidth">
                    <MailMjmlButton class="[&_p]:!mt-0" :background-color="theme.backgroundColor"
                        :color="theme.textColor" :inner-padding="theme.innerPadding" width="100%"
                        :align="theme.alignment" padding="0px" text-align="center" :class="theme.fontFamily"
                        :border-radius="theme.borderRadius as string" :font-size="theme.fontSize"
                        :font-weight="theme.fontWeight?.toString()" :line-height="theme.lineHeight?.toString()"
                        :letter-spacing="theme.letterSpacing?.toString()">
                        {{ index }}
                    </MailMjmlButton>
                </MailMjmlColumn>
            </template>
            <!-- Fallback Option -->
            <MailMjmlColumn :style="{
                display: store.formField?.type === 'rating' || (store.formField?.type === 'choice' && store.formField?.options?.length > 1)
                    ? 'none'
                    : undefined,
            }" width="100%">
                <MailMjmlButton class="[&_p]:!m-0" :background-color="theme.backgroundColor" :color="theme.textColor"
                    :inner-padding="theme.innerPadding" :width="theme.width" :align="theme.alignment"
                    :padding-bottom="theme.marginBottom as string" :class="theme.fontFamily"
                    :border-radius="theme.borderRadius as string" :font-size="theme.fontSize"
                    :font-weight="theme.fontWeight?.toString()" :line-height="theme.lineHeight?.toString()"
                    :letter-spacing="theme.letterSpacing?.toString()">
                    <NodeViewContent as="span" />
                </MailMjmlButton>
            </MailMjmlColumn>
        </MailMjmlSection>
        <MailMjmlSection :padding-top="'5px'" :padding-bottom="theme.marginBottom as string" :contenteditable="false" :style="{
            display:
                theme.preview !== 'rating' &&
                    store.formField?.type !== 'rating'
                    ? 'none'
                    : undefined,
        }" :css-class="node.attrs.identifier">
            <MailMjmlColumn v-for="[alignment, label] in labels" :width="`${100 / 3}%`">
                <MailMjmlText :align="alignment" :font-size="`${parseInt(theme.fontSize) - 3}px`"
                    :font-weight="theme.fontWeight?.toString()" :line-height="theme.lineHeight?.toString()"
                    :letter-spacing="theme.letterSpacing?.toString()">{{ label }}</MailMjmlText>
            </MailMjmlColumn>
        </MailMjmlSection>
    </NodeViewWrapper>
</template>

<style scoped></style>
